import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../services/auth.service';
import { ApiService } from '../../services/api.service';
import { ToasterService } from '../../services/toaster.service';
import { combineLatest, filter, Subject, take, takeUntil } from 'rxjs';
import { IconService } from '../../services/svg-icon.service';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface InstalledRelatedApp {
  platform: string;
  url?: string;
  id?: string;
}

interface RelatedAppsNavigator extends Navigator {
  getInstalledRelatedApps?: () => Promise<InstalledRelatedApp[]>;
}

@Component({
  selector: 'app-welcome',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatCardModule, MatIconModule],
  templateUrl: './welcome.component.html',
  styleUrls: ['./welcome.component.css'],
})
export class WelcomeComponent implements OnInit, OnDestroy {
  private readonly browserContinuationStorageKey = 'welcome-install-guide-dismissed';
  settingsError = false;
  installGuideVisible = false;
  mobilePlatform: 'android' | 'ios' | null = null;
  iosBrowser: 'edge' | 'other' | null = null;
  installedAppAvailable = false;
  androidInstallAvailable = false;
  androidMenuInstructionsVisible = false;
  installGuideReady = false;
  private deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
  private automaticPromptAttempted = false;
  private promptInProgress = false;
  private browserContinuationSelected = false;
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private toasterService: ToasterService,
    private iconService: IconService
  ) {}

  ngOnInit(): void {
    window.addEventListener('beforeinstallprompt', this.handleInstallPrompt);
    this.initializeInstallGuide();
    combineLatest([this.authService.isInitialized$, this.authService.isAuthenticated$])
      .pipe(
        filter(([isInitialized]) => isInitialized),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(([, isAuthenticated]) => {
        if (!isAuthenticated && !this.browserContinuationSelected) {
          sessionStorage.removeItem(this.browserContinuationStorageKey);
          this.initializeInstallGuide();
        }
      });
    this.apiService.settingsError$.pipe(takeUntil(this.destroy$)).subscribe((hasError) => {
      this.settingsError = hasError;
      if (hasError) {
        this.toasterService.error(
          'Unable to connect to the server.',
          'Close',
          0 // persist until dismissed
        );
      }
    });
  }

  login(): void {
    this.authService.login();
  }

  async installApp(): Promise<void> {
    if (!this.deferredInstallPrompt || this.promptInProgress) {
      return;
    }

    this.promptInProgress = true;
    try {
      await this.deferredInstallPrompt.prompt();
      const choice = await this.deferredInstallPrompt.userChoice;
      this.deferredInstallPrompt = null;
      this.androidInstallAvailable = false;

      if (choice.outcome === 'accepted') {
        this.installGuideVisible = false;
      } else {
        this.androidMenuInstructionsVisible = true;
      }
    } catch {
      // Some browsers require a user gesture and reject an automatic prompt.
      // Keep the button visible so the user can retry from a click.
      this.androidInstallAvailable = true;
    } finally {
      this.promptInProgress = false;
    }
  }

  continueInBrowser(): void {
    this.browserContinuationSelected = true;
    sessionStorage.setItem(this.browserContinuationStorageKey, 'true');
    this.installGuideVisible = false;
  }

  private async initializeInstallGuide(): Promise<void> {
    if (this.isRunningAsInstalledApp()) {
      return;
    }

    if (sessionStorage.getItem(this.browserContinuationStorageKey) === 'true') {
      this.installGuideVisible = false;
      this.installGuideReady = true;
      return;
    }

    const userAgent = navigator.userAgent;
    const isAndroid = /Android/i.test(userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);

    if (isAndroid) {
      this.mobilePlatform = 'android';
      this.installGuideVisible = true;
      this.installedAppAvailable = await this.isInstalledAppAvailable();
      this.installGuideReady = true;
      if (!this.installedAppAvailable) {
        await this.tryAutomaticInstallPrompt();
      }
    } else if (isIOS) {
      this.mobilePlatform = 'ios';
      this.iosBrowser = /EdgiOS/i.test(userAgent) ? 'edge' : 'other';
      this.installGuideVisible = true;
      this.installGuideReady = true;
    }
  }

  private async isInstalledAppAvailable(): Promise<boolean> {
    const relatedApps = (navigator as RelatedAppsNavigator).getInstalledRelatedApps;
    if (!relatedApps) {
      return false;
    }

    try {
      const installedApps = await relatedApps.call(navigator);
      return installedApps.some((app) => app.platform === 'webapp');
    } catch {
      return false;
    }
  }

  private readonly handleInstallPrompt = (event: Event): void => {
    event.preventDefault();
    this.deferredInstallPrompt = event as BeforeInstallPromptEvent;
    this.androidInstallAvailable = true;
    if (this.installGuideReady && !this.installedAppAvailable) {
      void this.tryAutomaticInstallPrompt();
    }
  };

  private async tryAutomaticInstallPrompt(): Promise<void> {
    if (this.automaticPromptAttempted || !this.deferredInstallPrompt || this.installedAppAvailable) {
      return;
    }

    this.automaticPromptAttempted = true;
    await this.installApp();
  }

  private isRunningAsInstalledApp(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone))
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeinstallprompt', this.handleInstallPrompt);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
